<script setup lang="ts">
  import { useLocale } from '@tundr/i18n';
  import tundrLoaderVideo from '@tundr/theme-assets/illustrations/tundr-loader.webm';
  import { usePreferredReducedMotion } from '@vueuse/core';
  import { computed } from 'vue';
  import { visuallyHiddenClass } from '../_shared/styles/visually-hidden.css';
  import {
    boneClass,
    boneWidthClasses,
    contentClass,
    footerBoneClass,
    iconBoneClass,
    layoutClass,
    loaderVideoClass,
    navItemClass,
    navLineBoneClass,
    rootClass,
    sidebarClass,
    sidebarDividerClass,
    sidebarFooterClass,
    sidebarHeaderClass,
    sidebarHeaderTextClass,
    sidebarNavClass,
    smallTextLineBoneClass,
    textLineBoneClass,
  } from './PageSkeleton.css';
  import type { PageSkeletonProps } from './PageSkeleton.types';

  const props = withDefaults(defineProps<PageSkeletonProps>(), {
    showSidebar: true,
  });

  const { t } = useLocale('ds-components');

  const accessibleLabel = computed(
    () => props.ariaLabel ?? t('page_skeleton.aria_label'),
  );
  const resolvedTestId = computed(() => props.testId ?? 'ds-page-skeleton');

  const reducedMotion = usePreferredReducedMotion();
  const prefersReducedMotion = computed(() => reducedMotion.value === 'reduce');

  type BoneWidth = keyof typeof boneWidthClasses;

  const SIDEBAR_NAV_WIDTHS: readonly BoneWidth[] = [
    'lg',
    'md',
    'xl',
    'sm',
    'lg',
    'md',
  ];
</script>

<template>
  <div :class="rootClass" role="status" :data-testid="resolvedTestId">
    <span :class="visuallyHiddenClass">{{ accessibleLabel }}</span>
    <div :class="layoutClass" aria-hidden="true">
      <div
        v-if="showSidebar"
        :class="sidebarClass"
        :data-testid="`${resolvedTestId}-sidebar`"
      >
        <div :class="sidebarHeaderClass">
          <div :class="sidebarHeaderTextClass">
            <span
              :class="[boneClass, textLineBoneClass, boneWidthClasses.lg]"
            />
            <span
              :class="[boneClass, smallTextLineBoneClass, boneWidthClasses.sm]"
            />
          </div>
        </div>
        <span :class="sidebarDividerClass" />
        <div :class="sidebarNavClass">
          <div
            v-for="(width, index) in SIDEBAR_NAV_WIDTHS"
            :key="index"
            :class="navItemClass"
          >
            <span :class="[boneClass, iconBoneClass]" />
            <span
              :class="[boneClass, navLineBoneClass, boneWidthClasses[width]]"
            />
          </div>
        </div>
        <div :class="sidebarFooterClass">
          <span :class="[boneClass, footerBoneClass]" />
        </div>
      </div>
      <div :class="contentClass" :data-testid="`${resolvedTestId}-content`">
        <video
          :class="loaderVideoClass"
          :src="tundrLoaderVideo"
          :autoplay="!prefersReducedMotion"
          :loop="!prefersReducedMotion"
          muted
          playsinline
        ></video>
      </div>
    </div>
  </div>
</template>
